import React, { useCallback, useEffect, useRef, useState } from "react";
import styles from '../Dashboard/index.module.css';
import { useDispatch, useSelector } from "react-redux";
import { addNewCourse, editCourse, getAllCourses } from "../../../redux/features/course/courseService";
import { showToast } from "../../../components/ToastMsg";
import BeatLoader from "react-spinners/BeatLoader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck, faCircleXmark, faXmark } from "@fortawesome/free-solid-svg-icons";
import Modal from "../../../components/Modal";
import Pagination from "../../../components/Pagination";
import NoResultsFound from "../../../components/NoResultsFound";
import TableSkeleton from "../../../components/Skeleton";

const Course = () => {

    const dispatch = useDispatch();

    const { isCoursesLoading, pageCount, courses } = useSelector((state) => state.course.allCourses);
    const { isCourseCreating } = useSelector((state) => state.course.newCourse);
    const { isEditingCourse } = useSelector((state) => state.course.editedCourse);

    const [formData, setFormData] = useState({
        name: '',
        code: '',
        certificate_entity_id: ''
    });

    const [errorMessages, setErrorMessages] = useState({});
    const [disabledBtn, setBtnDisable] = useState(false);
    const [skip, setSkip] = useState(0);

    const [isModalOpen, setModalOpen] = useState(false);
    const [selectedCourse, setSelectedCourse] = useState(null);
    const [initialCourse, setInitialCourse] = useState(null);
    const [editErrorMsgs, setEditErrorMsgs] = useState({
        name: '',
        course_code: ''
    });
    const [disabledEditBtn, setEditBtnDisable] = useState(false);
    const [errorMsg, setErrorMsg] = useState(null);
    const [selectedEditEntity, setSelectedEditEntity] = useState({
        id: '',
        name: ''
    });

    const fetchAllCourses = useCallback(() => {
        dispatch(getAllCourses());
    }, [dispatch]);

    useEffect(() => {
        fetchAllCourses();
    }, [fetchAllCourses]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));

        setErrorMessages((prev) => ({
            ...prev,
            [name]: '',
        }));
    };

    // Validate input
    const isValidCourse = () => {
        const errors = {};
        const trimmedName = formData.name.trim();
        if (!trimmedName) {
            errors.name = "Name is required.";
        }
        if (!formData.code) {
            errors.code = "Code is required.";
        }
        if (Object.keys(errors).length > 0) {
            setErrorMessages(errors);
            return false;
        } else {
            return true;
        }
    };

    const insertNewCourse = async () => {
        if (isValidCourse()) {
            setBtnDisable(true);
            try {
                const response = await dispatch(addNewCourse(formData)).unwrap();
                setBtnDisable(false);
                setFormData({
                    name: '',
                    code: '',
                });
                fetchAllCourses();
                showToast('success', response.message);
            }
            catch (error) {
                setBtnDisable(false);
                showToast('error', error.message);
            };
        }
    };

    // Edit course
    const handleSelectedCourseChange = (e) => {
        const { name, value } = e.target;
        setErrorMsg(null);
        setSelectedCourse((prev) => ({
            ...prev,
            [name]: value,
        }));

        setEditErrorMsgs((prev) => ({
            ...prev,
            [name]: '',
        }));
    };

    const isValidEditCourse = () => {
        const errors = {};
        const trimmedName = selectedCourse.name.trim();
        const trimmedCode = selectedCourse.code.trim();

        if (!trimmedName) {
            errors.name = "Name is required.";
        }
        if (!trimmedCode) {
            errors.course_code = "Course Code is required.";
        }
        if (Object.keys(errors).length > 0) {
            setEditErrorMsgs(errors);
            return false;
        } else {
            return true;
        }
    };

    function getChangedValues(selectedData) {
        const changedValues = {};
        for (const key in selectedData) {
            if (
                key !== 'id' &&
                selectedData.hasOwnProperty(key) &&
                selectedData[key] !== initialCourse[key]
            ) {
                changedValues[key] = selectedData[key];
            }

            if (key === 'id') {
                changedValues[key] = selectedData[key];
            }
        }
        return changedValues;
    }

    const editCourseDetails = async () => {
        if (isValidEditCourse(selectedCourse)) {
            const data = getChangedValues(selectedCourse);
            const hasChanges = Object.keys(data).some(key => key !== 'id');

            if (!hasChanges) {
                setErrorMsg('There are no changes to update.');
            } else {
                setEditBtnDisable(true);
                try {
                    const response = await dispatch(editCourse(data)).unwrap();
                    setEditBtnDisable(false);
                    setSelectedCourse(null);
                    setInitialCourse(null);
                    setModalOpen(false);
                    showToast('success', response.message);
                    fetchAllCourses();
                }
                catch (error) {
                    setEditBtnDisable(false);
                    showToast('error', error.message);
                };
            }
        }
    };

    //Activate Course
    const [isActivateModalOpen, setActivateModalOpen] = useState(false);
    const [isDeactivationModalOpen, setDeactivationModalOpen] = useState(false);
    const [selectedCourseId, setSelectedCourseId] = useState(null);

    const closeModal = () => setActivateModalOpen(false);
    const closDeactivationModal = () => setDeactivationModalOpen(false);

    const handleYes = async () => {
        setActivateModalOpen(false);
        try {
            const response = await dispatch(editCourse({ id: selectedCourseId, status: 'true' })).unwrap();
            setSelectedCourseId(null);
            setInitialCourse(null);
            showToast('success', response.message);
            fetchAllCourses();
        }
        catch (error) {
            showToast('error', error.message);
        };
    }
    const handleRejectYes = async () => {
        setDeactivationModalOpen(false);
        try {
            const response = await dispatch(editCourse({ id: selectedCourseId, status: 'false' })).unwrap();
            setSelectedCourseId(null);
            setInitialCourse(null);
            showToast('success', response.message);
            fetchAllCourses();
        }
        catch (error) {
            showToast('error', error.message);
        };
    }

    // Pagination
    const handlePageChange = (pageNumber) => {
        setSkip(pageNumber - 1);
    };

    return (
        <div className={`card-section`}>
            <div className={`main-card`}>
                <h1 className={`form-title`}>Add New Course</h1>
                <div className={styles.formContainer}>
                    <form
                        className={styles.coordinatorForm}
                        noValidate
                    >
                        <div className={`form-input-row`}>
                            <div className={`form-input-col md-col`}>
                                <div className={`form-input-group`}>
                                    <label htmlFor='full_name' className={`form-label`}>
                                        Name<span className={`required-star`}>*</span>
                                    </label>
                                    <input
                                        type='text'
                                        id='name'
                                        name='name'
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        placeholder='Enter Name'
                                        className={`form-input-default`}
                                    />
                                    {errorMessages.name && (
                                        <p className={`text-danger`}>{errorMessages.name}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className={`form-input-row`}>
                            <div className={`form-input-col md-col`}>
                                <div className={`form-input-group`}>
                                    <label htmlFor='full_name' className={`form-label`}>
                                        Code<span className={`required-star`}>*</span>
                                    </label>
                                    <input
                                        type='text'
                                        id='code'
                                        name='code'
                                        value={formData.code}
                                        onChange={handleInputChange}
                                        placeholder='Enter Code'
                                        className={`form-input-default`}
                                    />
                                    {errorMessages.code && (
                                        <p className={`text-danger`}>{errorMessages.code}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                        <button
                            className={`btn-primary`}
                            type='button'
                            disabled={disabledBtn}
                            onClick={insertNewCourse}
                        >
                            {isCourseCreating ? <BeatLoader color='#ffffff' /> : `Create`}
                        </button>
                    </form>
                </div>
            </div>
            <div className={`search-card`}>
                <div className={`search-card-header`}>
                    <h1 className={`form-title`}>Courses</h1>
                </div>
                <div className={`search-card-body`}>
                    {isCoursesLoading ?
                        <TableSkeleton columns={["Name", "Code", "Entity", "Status", "Actions"]} rowCount={10} height={20} />
                        : courses.length > 0 ?
                            <div className={`table-container`}>
                                <table className={`table`}>
                                    <thead className={`thead`}>
                                        <tr>
                                            <th className={`theadColumnOne`}>Name</th>
                                            <th>Code</th>
                                            <th>Entity</th>
                                            <th>Status</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className={`tbody`}>
                                        {courses?.map((course) => (
                                            <tr key={course.id}>
                                                <td className={`tdataColumnOne`}>
                                                    {course.name}
                                                </td>
                                                <td>{course.code}</td>
                                                <td>{course?.certificate_entity?.name}</td>
                                                <td>{course.status ? "Active" : "Inactive"}</td>
                                                <td className={`actionsBtn`}>
                                                    <button className={`btn-edit-label`} onClick={() => {
                                                        setSelectedCourse(course);
                                                        setInitialCourse(course);
                                                        setSelectedEditEntity({ id: course?.certificate_entity?.id, name: course?.certificate_entity?.name });
                                                        setEditBtnDisable(false);
                                                        setErrorMsg(null);
                                                        setEditErrorMsgs({
                                                            name: '',
                                                            course_code: ''
                                                        })
                                                        setModalOpen(true);
                                                    }}>Edit</button>
                                                    <button
                                                        className={course.status ? `btn-inactive` : `btn-active`}
                                                        onClick={() => {
                                                            setSelectedCourseId(course.id);
                                                            course.status ? setDeactivationModalOpen(true) : setActivateModalOpen(true);
                                                        }
                                                        }>{course.status ? 'Deactivate' : 'Acitvate'}</button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            :
                            <NoResultsFound />
                    }
                </div>
                <div className={`search-card-footer`}>
                    {pageCount > 0 && (
                        <div className={styles.paginationContainer}>
                            <Pagination
                                currentPage={skip + 1}
                                totalPages={pageCount}
                                onPageChange={handlePageChange}
                            />
                        </div>
                    )}
                </div>
            </div>
            {isModalOpen &&
                <div className={`modal-overlay`}>
                    <div className={`modal-card`}>
                        <div className={`modal-header`}>
                            <h4 className={`modal-title`}>Edit Course</h4>
                            <FontAwesomeIcon icon={faXmark} onClick={() => setModalOpen(false)} size="lg" cursor="pointer" />
                        </div>
                        <div className={`modal-body`}>
                            <div className={styles.formContainer}>
                                <form
                                    className={styles.coordinatorForm}
                                    noValidate
                                >
                                    <div className={`form-input-row md-col`}>
                                        <div className={`form-input-group`}>
                                            <label htmlFor='full_name' className={`form-label`}>
                                                Name<span className={`required-star`}>*</span>
                                            </label>
                                            <input
                                                type='text'
                                                id='name'
                                                name='name'
                                                value={selectedCourse.name || ''}
                                                onChange={handleSelectedCourseChange}
                                                placeholder='Enter Course Name'
                                                className={`form-input-default`}
                                            />
                                            {editErrorMsgs.name && (
                                                <p className={`text-danger`}>{editErrorMsgs.name}</p>
                                            )}
                                        </div>
                                    </div>
                                    <div className={`form-input-row md-col`}>
                                        <div className={`form-input-group`}>
                                            <label htmlFor='full_name' className={`form-label`}>
                                                Code<span className={`required-star`}>*</span>
                                            </label>
                                            <input
                                                type='text'
                                                id='code'
                                                name='code'
                                                value={selectedCourse.code || ''}
                                                onChange={handleSelectedCourseChange}
                                                placeholder='Enter Code'
                                                className={`form-input-default`}
                                            />
                                            {editErrorMsgs.code && (
                                                <p className={`text-danger`}>{editErrorMsgs.code}</p>
                                            )}
                                            {errorMsg && (
                                                <p className={`text-danger`}>{errorMsg}</p>
                                            )}
                                        </div>
                                    </div>
                                    {/* <div className={`form-input-row md-col`}>
                                        <div className={`form-input-group`}>
                                            <label htmlFor='full_name' className={`form-label`}>
                                                Entity<span className={`required-star`}>*</span>
                                            </label>
                                            <Dropdown
                                                options={activeCertificateEntities}
                                                placeholder="Choose an Option"
                                                onSelect={handleEditSelect}
                                                value={selectedEditEntity.name}
                                            />
                                            {editErrorMsgs.certificate_entity_id && (
                                                <p className={`text-danger`}>{editErrorMsgs.certificate_entity_id}</p>
                                            )}
                                        </div>
                                    </div> */}
                                    <button
                                        className={`btn-primary`}
                                        type='button'
                                        disabled={disabledEditBtn}
                                        onClick={editCourseDetails}
                                    >
                                        {isEditingCourse ? <BeatLoader color='#ffffff' /> : `Save`}
                                    </button>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            }
            {isActivateModalOpen && (
                <Modal
                    title='Course Activation'
                    onClose={closeModal}
                    onYes={handleYes}
                    isInput={false}
                    iconName={faCircleCheck}
                    bodyText={`Are you sure you want to activate the Course?`}
                    colorType={`var(--green)`}
                />
            )}
            {isDeactivationModalOpen && (
                <Modal
                    title='Course Deactivation'
                    onClose={closDeactivationModal}
                    onYes={handleRejectYes}
                    isInput={false}
                    iconName={faCircleXmark}
                    bodyText={`Are you sure you want to deactivate the Course?`}
                    colorType={`var(--danger)`}
                />
            )}
        </div>
    );
};

export default Course;